import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader, Numeric } from '@blade/ui-kit'
import { authStateAtom } from '@root/entities/auth/atoms'
import { basicModeAtom } from '@root/entities/basic'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui/currency-icon'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import {
  BASIC_QUICK_BUY_NETWORKS,
  BasicQuickBuyButton,
} from '@root/features/basic'
import { quoteSubscriptionRegistry } from '@root/features/quote/atoms/reatom-quote-registry'
import { reatomQuickBuy } from '@root/features/trade/quick-buy/atoms/reatom-quick-buy'
import { quickSwapAmountBySectionAtom } from '@root/features/trade/trade-settings'
import { tradingWalletAtom } from '@root/features/trade/trading/atoms/trading-wallet'
import {
  environmentAtom,
  isTauriOrBrowserLocalAtom,
  PlatformUtil,
} from '@root/shared'
import { TgUtil } from '@root/shared/lib/telegram'
import { useTranslation } from '@root/shared/localization'
import {
  ChargeExactIcon,
  CheckCircleIcon,
  CrossCircleIcon,
} from '@root/shared/ui-kit'

import { QuoteMinAmountTooltip } from './quote-min-amount-tooltip'
import { useQuoteDevStatus } from './use-quote-dev-status'

import type { PairModel } from '@root/entities/pair'
import type { TradeSettingsQuick } from '@root/entities/trade-settings'
import type { ClassNameProps } from '@root/shared/react'

type QuickBuyButtonProps = {
  withLabel?: boolean
  withAmount?: boolean
  withIcon?: boolean
  pair: PairModel
  section: TradeSettingsQuick.QuickSwapSection
} & ClassNameProps

const LOGO_SIZE = 16
const RESET_DELAY = 3000

const AdvancedQuickBuyButton: React.FC<QuickBuyButtonProps> = ({
  className,
  withAmount,
  withIcon,
  withLabel,
  pair,
  section,
}) => {
  const { t } = useTranslation()
  const [network] = useAtom(selectedNetworkAtom)
  const [isLoggedIn] = useAtom(authStateAtom.isLoggedIn)
  const [environment] = useAtom(environmentAtom)
  const [isTauri] = useAtom(isTauriOrBrowserLocalAtom)
  const [amount] = useAtom(
    (ctx) => ctx.spy(quickSwapAmountBySectionAtom)?.[section].buy,
    [section],
  )
  const [onOurSide] = useAtom((ctx) => {
    const wallet = ctx.spy(tradingWalletAtom)
    return !!wallet && ctx.spy(wallet.custody) === 'server'
  }, [])
  const ctx = useCtx()
  const [quoteModel] = useAtom(
    (ctx) => {
      if (!amount) {
        return undefined
      }
      const model = quoteSubscriptionRegistry.watch(ctx, {
        pair: pair,
        type: 'buy',
        amount,
      })
      return model
    },
    [amount, pair],
  )

  useEffect(() => {
    if (quoteModel) {
      quoteModel.subscribe(ctx)
    }
  }, [ctx, pair.quote.ticker, quoteModel, section])

  const buttonColors = colorMap(isLoggedIn ? network : undefined)

  const requestAtom = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomQuickBuy({
        pair,
        section,
      }),
    [pair, section],
  )
  const [requestState] = useAtom(requestAtom)
  const [uiState, setUiState] =
    useState<AsyncStateN.AsyncStateN<unknown, unknown>>(requestState)

  useEffect(() => {
    if (AsyncStateN.isReady(requestState)) {
      setUiState(requestState)
      const timeout = setTimeout(() => {
        setUiState(AsyncStateN.notInitiated())
      }, RESET_DELAY)
      return () => clearTimeout(timeout)
    }
    setUiState(requestState)
  }, [requestState])

  const performBuy = useAction(requestAtom.execute)

  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()

    /*
     * A trade this browser cannot make goes to the bot.
     *
     * A wallet whose key we keep is traded on our side, so it falls through
     * and buys here. A device wallet cannot be signed for in a browser, and
     * what used to be offered instead was the download dialog - which in both
     * deployed builds points its Download button at `undefined`, because
     * VITE_DOWNLOAD_URL is set in neither image, and deep-links to a desktop
     * build nobody publishes. The bot is where that trade can actually
     * happen.
     */
    if (!isTauri && !onOurSide) {
      PlatformUtil.openLink(
        TgUtil.createTelegramLink(environment.buybotUsername),
      )
      return
    }

    performBuy()
  }

  const { quoteDevStatus } = useQuoteDevStatus({ quoteModel })

  const buttonContent = isLoggedIn ? (
    <QuoteMinAmountTooltip model={quoteModel} currency={pair.quote}>
      <Content data-hidden={!AsyncStateN.isNotInitiated(uiState)}>
        {withIcon ? (
          <WithIcon>
            <ChargeExactIcon size={14} />
          </WithIcon>
        ) : undefined}
        {withLabel ? <Label>{t('trading.buy')}</Label> : undefined}
        {withAmount && amount ? (
          <WithAmount>
            <AmountValue>
              <Numeric value={amount} preset="currency-amount-short" />
            </AmountValue>
            <CurrencyNetworkIcon
              network={network}
              size={LOGO_SIZE}
              iconRatio={0.8}
            />
          </WithAmount>
        ) : undefined}
      </Content>
    </QuoteMinAmountTooltip>
  ) : (
    <Content>
      <WithIcon>
        <ChargeExactIcon size={14} />
      </WithIcon>
      <Label>{t('trading.buy')}</Label>
    </Content>
  )

  const renderButtonContent = () => {
    return AsyncStateN.match(uiState, {
      Loading: () => (
        <StateContainer>
          <CircularLoader size={16} />
        </StateContainer>
      ),
      Success: () => {
        return (
          <StateContainer>
            <CheckCircleIcon size={16} />
          </StateContainer>
        )
      },
      Error: () => {
        return (
          <StateContainer>
            <CrossCircleIcon size={16} />
          </StateContainer>
        )
      },
      NotInitiated: () => <></>,
    })
  }

  return (
    <Root
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      disabled={AsyncStateN.isNotInitiated(uiState) === false}
      className={className}
      aria-disabled={!isLoggedIn}
      css={css`
        --buy-button-border: 1px solid ${buttonColors.border};
        --buy-button-color: ${buttonColors.color};
        --buy-button-bg: ${buttonColors.background};
        --buy-button-bg-hover: ${buttonColors.backgroundHover};
      `}
      data-state={AsyncStateN.match(uiState, {
        Loading: () => 'loading',
        Success: () => 'success',
        Error: () => 'error',
        NotInitiated: () => 'not-initiated',
      })}
      data-dev-quote={quoteDevStatus}
    >
      {buttonContent}
      {renderButtonContent()}
    </Root>
  )
}

/**
 * Quick buy, in whichever money the account trades with.
 *
 * Basic spends dollars from its balance; advanced spends the native coin from
 * a wallet. The same button sits on every card and table row, so choosing
 * here changes them all at once. Its own branch rather than an early return,
 * so neither side's hooks depend on the mode.
 */
export const QuickBuyButton: React.FC<QuickBuyButtonProps> = (props) => {
  const [tradingMode] = useAtom(basicModeAtom)

  if (tradingMode === 'basic') {
    const network = Network.toDto(props.pair.network)

    // A chain Basic cannot land a buy on gets no button, rather than one
    // that is refused every time it is pressed.
    return BASIC_QUICK_BUY_NETWORKS.has(network) ? (
      <BasicQuickBuyButton
        className={props.className}
        withAmount={props.withAmount}
        withIcon={props.withIcon}
        withLabel={props.withLabel}
        target={{
          network,
          tokenAddress: props.pair.quote.address,
          symbol: props.pair.quote.ticker,
        }}
      />
    ) : undefined
  }

  return <AdvancedQuickBuyButton {...props} />
}

// TODO: move to theme colors
const colorMap = (network?: Network.Network) =>
  match(network)
    .with(Network.Ethereum, () => ({
      color: '#5F97FF',
      border: 'rgba(95, 151, 255, 0.16)',
      background: 'rgba(95, 151, 255, 0.06)',
      backgroundHover: 'rgba(95, 151, 255, 0.22)',
    }))
    .with(Network.Bsc, () => ({
      color: '#FFD34D',
      border: 'rgba(255, 211, 77, 0.19)',
      background: 'rgba(255, 211, 77, 0.08)',
      backgroundHover: 'rgba(255, 211, 77, 0.32)',
    }))
    .with(Network.Base, () => ({
      color: '#246AFF',
      border: 'rgba(36, 106, 255, 0.19)',
      background: 'rgba(36, 106, 255, 0.08)',
      backgroundHover: 'rgba(36, 106, 255, 0.32)',
    }))
    .with(Network.Solana, () => ({
      spinner: 'purple',
      color: '#A781FF',
      border: 'rgba(167, 129, 255, 0.18)',
      background: 'rgba(167, 129, 255, 0.08)',
      backgroundHover: 'rgba(167, 129, 255, 0.25)',
    }))
    .with(Network.Ton, () => ({
      color: '#0098E9',
      border: 'rgba(0, 152, 233, 0.13)',
      background: 'rgba(0, 152, 233, 0.05)',
      backgroundHover: 'rgba(0, 152, 233, 0.17)',
    }))
    .otherwise(() => ({
      color: '#6e8aff',
      border: 'rgba(62, 99, 255, 0.45)',
      background: 'rgba(62, 99, 255, 0.2)',
      backgroundHover: 'rgba(62, 99, 255, 0.3)',
    }))

const Root = styled.button`
  pointer-events: auto;
  border: var(--buy-button-border);
  background: var(--buy-button-bg);
  position: relative;

  outline: none;
  text-transform: uppercase;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
  gap: 2px;

  text-align: center;
  font-size: 11px;
  font-weight: 600;
  transition: background 0.1s ease-in-out;
  white-space: nowrap;
  height: var(--buy-button-height, 28px);
  padding-inline: 8px;
  border-radius: 6px;

  &:hover {
    background: var(--buy-button-bg-hover);
  }
  &:not(:disabled) {
    cursor: pointer;
  }
  &[aria-disabled='true'] {
    opacity: 0.7;
  }

  &[data-state='loading'] {
    svg {
      color: var(--buy-button-color);
    }
  }
  &[data-state='success'] {
    border: 1px solid var(--successA-2);
    background: var(--successA-1);
    svg {
      color: var(--success-9);
    }
  }
  &[data-state='error'] {
    border: 1px solid var(--errorA-3);
    background: var(--errorA-2);
    svg {
      color: var(--error-9);
    }
  }

  /* DEV-only pathfinder quote-state ring */
  &[data-dev-quote='none'] {
    box-shadow: 0 0 0 2px var(--candleRed-9) inset;
  }
  &[data-dev-quote='waiting'] {
    box-shadow: 0 0 0 2px var(--amber-9) inset;
  }
  &[data-dev-quote='timeout'] {
    box-shadow: 0 0 0 2px var(--purple-9) inset;
  }
  &[data-dev-quote='ready'] {
    box-shadow: 0 0 0 2px var(--candleGreen-9) inset;
  }
`

const Label = styled.div`
  color: var(--buy-button-color);
  text-align: center;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;

  html[lang='ru']:not([data-platform]) & {
    font-size: 9px;
    white-space: break-spaces;
  }
`

const WithIcon = styled.div`
  color: var(--buy-button-color);
  display: grid;
`

const WithAmount = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 5px;
  align-items: center;
  justify-content: center;
`

const AmountValue = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
`

const Content = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
  gap: 2px;

  &[data-hidden='true'] {
    visibility: hidden;
  }
`

const StateContainer = styled.div`
  position: absolute;
  inset: 0;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
`
